import { useState, useRef, useCallback } from 'react'
import type { CSSProperties } from 'react'
import { useGeocoder, type GeoResult } from '../../hooks/useGeocoder'
import { COLORS, FONT, SPACING, RADIUS } from '../ui/tokens'
import type { Coords } from '../../types/geo'

interface Props {
  label: string
  placeholder: string
  value: string
  onChange: (name: string, coords: Coords | null) => void
  gpsCoords?: Coords | null
}

export function LocationAutocomplete({ label, placeholder, value, onChange, gpsCoords }: Props) {
  const { search } = useGeocoder()
  const [suggestions, setSuggestions] = useState<GeoResult[]>([])
  const [focused, setFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleInput = useCallback((text: string) => {
    onChange(text, null)
    search(text, setSuggestions)
  }, [onChange, search])

  const handleSelect = useCallback((result: GeoResult) => {
    onChange(result.name, result.coords)
    setSuggestions([])
    setFocused(false)
    inputRef.current?.blur()
  }, [onChange])

  const handleGPS = useCallback(() => {
    if (!gpsCoords) return
    onChange('Моё местоположение', gpsCoords)
    setSuggestions([])
  }, [gpsCoords, onChange])

  const inputStyle: CSSProperties = {
    width: '100%', padding: '12px 14px', backgroundColor: COLORS.bgElevated,
    border: `1px solid ${focused ? COLORS.accent : COLORS.border}`, borderRadius: RADIUS.md,
    color: COLORS.textPrimary, fontSize: FONT.base, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s',
  }

  const showDropdown = focused && (suggestions.length > 0 || gpsCoords)

  return (
    <div style={{ position: 'relative' }}>
      <label style={{ fontSize: FONT.xs, color: COLORS.textSecond, display: 'block', marginBottom: 4 }}>{label}</label>
      <input
        ref={inputRef} style={inputStyle} placeholder={placeholder} value={value}
        onChange={(e) => handleInput(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        autoComplete="off" autoCorrect="off" spellCheck={false}
      />
      {showDropdown && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: COLORS.bgCard,
          border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.md, zIndex: 200, marginTop: 4,
          overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        }}>
          {gpsCoords && (
            <button onMouseDown={(e) => { e.preventDefault(); handleGPS() }} style={{
              width: '100%', padding: `${SPACING.sm}px ${SPACING.md}px`, backgroundColor: 'transparent',
              border: 'none', borderBottom: suggestions.length ? `1px solid ${COLORS.border}` : 'none',
              color: COLORS.accent, fontSize: FONT.sm, textAlign: 'left', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: SPACING.xs,
            }}>
              <span>📍</span><span>Моё местоположение</span>
            </button>
          )}
          {suggestions.map((r, i) => (
            <button key={i} onMouseDown={(e) => { e.preventDefault(); handleSelect(r) }} style={{
              width: '100%', padding: `${SPACING.sm}px ${SPACING.md}px`, backgroundColor: 'transparent', border: 'none',
              borderBottom: i < suggestions.length - 1 ? `1px solid ${COLORS.border}` : 'none',
              color: COLORS.textPrimary, fontSize: FONT.sm, textAlign: 'left', cursor: 'pointer',
            }}>
              <div style={{ fontWeight: 500 }}>{r.name}</div>
              <div style={{ fontSize: FONT.xs, color: COLORS.textSecond, marginTop: 2 }}>
                {r.displayName.split(',').slice(0, 3).join(',')}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
